import { FC, useRef, useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faGripVertical,
    faEdit,
    faToggleOn,
    faToggleOff,
    faCloudUploadAlt,
    faImage,
} from '@fortawesome/free-solid-svg-icons'
import { faTrashCan } from '@fortawesome/free-regular-svg-icons'
import { useAppSelector } from '../../../../../store/hooks'
import { SortableSectionProps } from '../types'
import HtmlEditor from '../../../../common/forms/fields/HtmlEditor'

const SortableSection: FC<SortableSectionProps> = ({
    item,
    index,
    isSelected,
    onSelect,
    onEdit,
    onRemove,
    onToggleActive,
    onContentChange,
    onImageChange,
    onNameChange,
    activeLocale,
    devicePreview,
    formikProps,
    translations,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isHovered, setIsHovered] = useState(false)
    const [editingName, setEditingName] = useState(false)
    const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const pillar = item.pagePart as {
        page_content_name?: string
        page_content_value?: string | Record<string, string>
        page_content_id?: number
        img_url?: string | null
        image_url?: string | null
        media_store_id?: number | null
        active?: boolean | number
        page_content_active?: boolean | number
        translations?: Record<string, Record<string, string>>
    }

    const sectionName = pillar?.page_content_name || 'Unnamed Section'
    const activeFlag = pillar?.page_content_active ?? pillar?.active
    const isActive = activeFlag !== false && activeFlag !== 0

    const langId = activeLocale
        ? initData.languages.data[activeLocale]?.id
        : null

    let htmlContent = ''
    const valueObj = pillar?.page_content_value
    if (valueObj && typeof valueObj === 'object') {
        if (langId) {
            htmlContent = valueObj['_' + langId] || ''
        }
        if (!htmlContent) {
            const firstKey = Object.keys(valueObj)[0]
            if (firstKey) htmlContent = valueObj[firstKey] || ''
        }
    } else if (typeof valueObj === 'string' && pillar?.translations) {
        const localeKey = activeLocale || Object.keys(pillar.translations)[0]
        if (localeKey) {
            htmlContent = pillar.translations[localeKey]?.[valueObj] || ''
        }
    }

    const imageUrl = pillar?.image_url || pillar?.img_url || null
    const isOdd = (index + 1) % 2 !== 0
    const isFirst = index === 0
    const isEven = index % 2 === 0
    const isMobile = devicePreview === 'mobile'
    const isTablet = devicePreview === 'tablet'
    const isCompact = isMobile || isTablet

    const handleMouseEnter = () => {
        if (hoverTimeout.current) {
            clearTimeout(hoverTimeout.current)
            hoverTimeout.current = null
        }
        setIsHovered(true)
    }

    const handleMouseLeave = () => {
        hoverTimeout.current = setTimeout(() => {
            setIsHovered(false)
            setEditingName(false)
        }, 300)
    }

    const handleContentChange = (newContent: string) => {
        onContentChange(item.id as string, activeLocale, newContent)
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            onImageChange(item.id as string, file)
        }
    }

    const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setEditingName(false)
        if (e.target.value !== sectionName) {
            onNameChange(item.id as string, e.target.value)
        }
    }

    const renderImageArea = () => {
        if (!imageUrl && !isHovered) return null

        return (
            <div className="sfy-live-section__image-col">
                {imageUrl ? (
                    <div className="sfy-live-section__image-wrapper">
                        <img
                            src={imageUrl}
                            alt={sectionName}
                            className="sfy-live-section__image"
                        />
                        {isHovered && (
                            <div
                                className="sfy-live-section__image-upload-overlay"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <FontAwesomeIcon icon={faCloudUploadAlt} />
                                <span>Change Image</span>
                            </div>
                        )}
                    </div>
                ) : (
                    isHovered && (
                        <div
                            className="sfy-live-section__image-placeholder"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FontAwesomeIcon icon={faImage} />
                            <span>Upload Image</span>
                        </div>
                    )
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handleImageUpload}
                />
            </div>
        )
    }

    const renderContent = () => {
        if (isHovered) {
            return (
                <div className="sfy-live-section__editor-wrapper">
                    <HtmlEditor
                        value={htmlContent}
                        onChange={handleContentChange}
                        placeholder="Write your section content here..."
                    />
                </div>
            )
        }

        if (!htmlContent) {
            return (
                <div className="sfy-live-section__empty-content">
                    <p>{sectionName}</p>
                    <span>Hover to edit content</span>
                </div>
            )
        }

        return (
            <div
                className="sfy-live-section__prose"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        )
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`sfy-live-section ${
                isEven
                    ? 'sfy-live-section--even'
                    : 'sfy-live-section--odd'
            } ${isSelected ? 'sfy-live-section--selected' : ''} ${
                isDragging ? 'sfy-live-section--dragging' : ''
            } ${!isActive ? 'sfy-live-section--inactive' : ''} ${
                isHovered ? 'sfy-live-section--editing' : ''
            }`}
            onClick={() => onSelect(item.id as string)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className={`sfy-live-section__hover-controls ${isHovered ? 'sfy-live-section__hover-controls--visible' : ''}`}>
                <div className="sfy-live-section__controls-left">
                    <span
                        className="sfy-live-section__drag-handle"
                        {...attributes}
                        {...listeners}
                        title="Drag to reorder"
                    >
                        <FontAwesomeIcon icon={faGripVertical} />
                    </span>
                    {editingName ? (
                        <input
                            type="text"
                            className="sfy-live-section__name-input"
                            defaultValue={sectionName}
                            onBlur={handleNameBlur}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                            }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <span
                            className="sfy-live-section__section-name"
                            onClick={(e) => {
                                e.stopPropagation()
                                setEditingName(true)
                            }}
                            title="Click to rename"
                        >
                            {sectionName}
                            <FontAwesomeIcon icon={faEdit} className="ms-1" style={{ fontSize: '0.55rem', opacity: 0.7 }} />
                        </span>
                    )}
                </div>
                <div className="sfy-live-section__controls-right">
                    <button
                        type="button"
                        className="sfy-live-section__control-btn sfy-live-section__control-btn--edit"
                        onClick={(e) => {
                            e.stopPropagation()
                            if (pillar?.page_content_id) {
                                onEdit(pillar.page_content_id)
                            }
                        }}
                        title="Open full editor"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                        type="button"
                        className={`sfy-live-section__control-btn sfy-live-section__control-btn--toggle ${
                            isActive ? 'sfy-live-section__control-btn--active' : ''
                        }`}
                        onClick={(e) => {
                            e.stopPropagation()
                            onToggleActive(item.id as string)
                        }}
                        title={isActive ? 'Deactivate section' : 'Activate section'}
                    >
                        <FontAwesomeIcon icon={isActive ? faToggleOn : faToggleOff} />
                    </button>
                    <button
                        type="button"
                        className="sfy-live-section__control-btn sfy-live-section__control-btn--delete"
                        onClick={(e) => {
                            e.stopPropagation()
                            onRemove(item.id as string)
                        }}
                        title="Remove section"
                    >
                        <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                </div>
            </div>

            <div className={`sfy-live-section__body ${isFirst ? 'sfy-live-section__body--first' : ''}`}>
                <div className="sfy-live-section__container">
                    {(imageUrl || isHovered) ? (
                        <div className={`sfy-live-section__grid ${isCompact ? 'sfy-live-section__grid--stacked' : ''}`}>
                            {isOdd ? (
                                <>
                                    <div className="sfy-live-section__text-col">
                                        {renderContent()}
                                    </div>
                                    {renderImageArea()}
                                </>
                            ) : (
                                <>
                                    {renderImageArea()}
                                    <div className="sfy-live-section__text-col">
                                        {renderContent()}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="sfy-live-section__text-only">
                            {renderContent()}
                        </div>
                    )}
                </div>
            </div>

            {!isActive && (
                <div className="sfy-live-section__inactive-overlay">
                    <span>Section Inactive</span>
                </div>
            )}
        </div>
    )
}

export default SortableSection
